/**
 * TemplateCard Component
 * 
 * Displays individual report template information in a card format
 * with actions for preview, use, edit, and manage operations
 * Matches backend ReportTemplateResponse schema
 */

import React, { useState } from 'react';
import { Card, Badge, Dropdown, Button, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { 
    BsBarChart, 
    BsThreeDotsVertical, 
    BsEye, 
    BsFileText, 
    BsPencil, 
    BsCopy,
    BsTrash,
    BsClock,
    BsPerson,
    BsGraphUp,
    BsShield,
    BsTag
} from 'react-icons/bs';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { deleteTemplate, fetchTemplateById, cloneTemplate } from '../../../redux/slices/reportingSlice';
import { setActiveModal } from '../../../redux/slices/reportingSlice';
import reportingService from '../../../services/reportingService';

const TemplateCard = ({ 
    template, 
    viewMode = 'grid',
    onPreview,
    onUse,
    onEdit,
    onClone,
    onDelete,
    isSelected = false,
    onSelect,
    isLoading = false 
}) => {
    const dispatch = useDispatch();
    const [actionLoading, setActionLoading] = useState(null);

    // Get category badge color
    const getCategoryBadge = (category) => {
        const categoryMap = {
            patient: 'primary',
            clinical: 'success', 
            financial: 'warning',
            operational: 'info',
            administrative: 'secondary'
        };
        return categoryMap[category] || 'primary';
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    // Handle actions with loading states
    const handleAction = async (action, actionFn) => {
        if (!actionFn) return;
        
        setActionLoading(action);
        try {
            await actionFn(template);
        } finally {
            setActionLoading(null);
        }
    };

    // Handle template usage (generate report from template)
    const handleUseTemplate = () => {
        if (onUse) {
            onUse(template);
        } else {
            // Open report generation modal with this template
            dispatch(setActiveModal({ type: 'create-report', templateId: template.id }));
        }
    };

    // Handle preview
    const handlePreview = async () => {
        if (onPreview) {
            onPreview(template);
        } else {
            setActionLoading('preview');
            try {
                await reportingService.previewTemplate(template.id);
            } catch (error) {
            } finally {
                setActionLoading(null);
            }
        }
    };

    // Handle clone
    const handleClone = async () => {
        if (onClone) {
            onClone(template);
        } else {
            setActionLoading('clone');
            try {
                await dispatch(cloneTemplate({ 
                    templateId: template.id, 
                    cloneData: { name: `${template.name} (Copy)` }
                }));
            } catch (error) {
            } finally {
                setActionLoading(null);
            }
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            if (onDelete) {
                onDelete(template);
            } else {
                try {
                    await dispatch(deleteTemplate(template.id));
                } catch (error) {
                }
            }
        }
    };

    if (viewMode === 'list') {
        return (
            <Card className={`template-card-list border-0 shadow-sm mb-2 ${isSelected ? 'border-primary' : ''}`}>
                <Card.Body className="py-3">
                    <div className="row align-items-center">
                        <div className="col-md-1">
                            {onSelect && (
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => onSelect(template.id, e.target.checked)}
                                    className="form-check-input"
                                />
                            )}
                        </div>
                        <div className="col-md-1">
                            <div className="template-icon">
                                <BsBarChart className="h4 text-success mb-0" />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <h6 className="mb-1 fw-semibold">{template.name}</h6>
                            <div className="d-flex gap-1">
                                <Badge bg={getCategoryBadge(template.category)} size="sm">
                                    {template.category}
                                </Badge>
                                {template.subcategory && (
                                    <Badge bg="light" text="dark" size="sm">
                                        {template.subcategory}
                                    </Badge>
                                )}
                            </div>
                        </div>
                        <div className="col-md-3">
                            <small className="text-muted">
                                {typeof template.description === 'string' 
                                    ? (template.description.length > 80 
                                        ? template.description.substring(0, 80) + '...' 
                                        : template.description)
                                    : 'No description available'
                                }
                            </small>
                        </div>
                        <div className="col-md-2">
                            <div className="text-muted small">
                                <div>{Array.isArray(template.available_filters) ? template.available_filters.length : 0} filters</div>
                                <div className="d-flex align-items-center">
                                    <BsShield className="me-1" />
                                    {Array.isArray(template.required_permissions) ? template.required_permissions.length : 0} perms
                                </div>
                            </div>
                        </div>
                        <div className="col-md-1">
                            <div className="text-center">
                                {template.is_active ? (
                                    <Badge bg="success">Active</Badge>
                                ) : (
                                    <Badge bg="secondary">Inactive</Badge>
                                )}
                            </div>
                        </div>
                        <div className="col-md-1">
                            <Dropdown>
                                <Dropdown.Toggle variant="link" size="sm" className="text-muted">
                                    <BsThreeDotsVertical />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleUseTemplate}>
                                        <BsFileText className="me-2" />
                                        Use Template
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handlePreview} disabled={actionLoading === 'preview'}>
                                        <BsEye className="me-2" />
                                        Preview
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleClone} disabled={actionLoading === 'clone'}>
                                        <BsCopy className="me-2" />
                                        Clone
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={handleDelete} className="text-danger">
                                        <BsTrash className="me-2" />
                                        Delete
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    // Grid view (default)
    return (
        <Card className={`template-card border-0 shadow-sm h-100 ${isSelected ? 'border-primary' : ''}`}>
            <Card.Header className="bg-transparent border-0 pb-0">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="template-category">
                        <Badge bg={getCategoryBadge(template.category)} size="sm">
                            {template.category}
                        </Badge>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {template.is_active ? (
                            <Badge bg="success" size="sm">Active</Badge>
                        ) : (
                            <Badge bg="secondary" size="sm">Inactive</Badge>
                        )}
                        {onSelect && (
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => onSelect(template.id, e.target.checked)}
                                className="form-check-input"
                            />
                        )}
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="pt-2">
                <div className="text-center mb-3">
                    <BsBarChart className="display-6 text-success mb-2" />
                    <h6 className="fw-semibold mb-1">{template.name}</h6>
                    <small className="text-muted">ID: {template.id}</small>
                </div>

                <div className="template-details small text-muted mb-3">
                    <p className="mb-2">
                        {typeof template.description === 'string' 
                            ? (template.description.length > 100 
                                ? template.description.substring(0, 100) + '...' 
                                : template.description)
                            : 'No description available'
                        }
                    </p>
                    
                    <div className="template-stats">
                        <div className="d-flex justify-content-between">
                            <span>Filters:</span>
                            <span>{Array.isArray(template.available_filters) ? template.available_filters.length : 0}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span>Permissions:</span>
                            <span>{Array.isArray(template.required_permissions) ? template.required_permissions.length : 0}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span>Created:</span>
                            <span>{formatDate(template.created_at)}</span>
                        </div>
                        {template.subcategory && (
                            <div className="d-flex justify-content-between">
                                <span>Subcategory:</span>
                                <span>{template.subcategory}</span>
                            </div>
                        )}
                    </div>
                </div>

                {template.required_permissions?.length > 0 && (
                    <div className="mb-3">
                        <div className="d-flex align-items-center gap-1 text-muted small">
                            <BsShield size={12} />
                            <span>Requires permissions</span>
                        </div>
                        <div className="mt-1">
                            {template.required_permissions.slice(0, 2).map((perm, index) => (
                                <Badge key={index} bg="light" text="dark" size="sm" className="me-1">
                                    {perm}
                                </Badge>
                            ))}
                            {template.required_permissions.length > 2 && (
                                <Badge bg="light" text="dark" size="sm">
                                    +{template.required_permissions.length - 2} more
                                </Badge>
                            )}
                        </div>
                    </div>
                )}
            </Card.Body>
            <Card.Footer className="bg-transparent border-0 pt-0">
                <div className="d-grid gap-2">
                    <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={handleUseTemplate}
                        disabled={!template.is_active || actionLoading === 'use'}
                    >
                        {actionLoading === 'use' ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Creating...
                            </>
                        ) : (
                            <>
                                <BsFileText className="me-1" />
                                Use Template
                            </>
                        )}
                    </Button>
                    <div className="d-flex gap-2">
                        <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            className="flex-fill"
                            onClick={handlePreview}
                            disabled={actionLoading === 'preview'}
                        >
                            <BsEye />
                        </Button>
                        <Button 
                            variant="outline-success" 
                            size="sm" 
                            className="flex-fill"
                            onClick={handleClone}
                            disabled={actionLoading === 'clone'}
                        >
                            <BsCopy />
                        </Button>
                        <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" size="sm" className="border-0">
                                <BsThreeDotsVertical />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={onEdit}>
                                    <BsPencil className="me-2" />
                                    Edit
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={handleDelete} className="text-danger">
                                    <BsTrash className="me-2" />
                                    Delete
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </Card.Footer>
        </Card>
    );
};

TemplateCard.propTypes = {
    template: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        category: PropTypes.string.isRequired,
        subcategory: PropTypes.string,
        available_filters: PropTypes.arrayOf(PropTypes.string).isRequired,
        required_permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
        is_active: PropTypes.bool.isRequired,
        created_at: PropTypes.string.isRequired
    }).isRequired,
    viewMode: PropTypes.oneOf(['grid', 'list']),
    onPreview: PropTypes.func,
    onUse: PropTypes.func,
    onEdit: PropTypes.func,
    onClone: PropTypes.func,
    onDelete: PropTypes.func,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    isLoading: PropTypes.bool
};

export default TemplateCard;